import classNames from 'classnames';
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useOnValueChange } from '../../hooks/useOnValueChange';
import { useStableEvent } from '../../hooks/useStableEvent';

interface TabPaneProps extends Omit<React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>, 'key' | 'onClick'> {
  key: string;
  onClick?: ({ key }: { key: string }) => void;
  selected?: boolean;
  customKey?: string;
}
const TabPane_ = React.forwardRef<HTMLDivElement, TabPaneProps>(({ className, onClick: onClickProps, selected, customKey, ...props }, ref) => {
  const onClick = useCallback(() => {
    if (onClickProps && customKey) onClickProps({ key: customKey });
  }, [customKey, onClickProps]);
  return <div ref={ref} className={classNames('flex items-center justify-center cursor-pointer text-sm font-semibold whitespace-nowrap overflow-hidden px-2', selected && 'dark:text-white', className)} {...props} onClick={onClick}></div>;
});
TabPane_.displayName = 'TabPane';
export const TabPane = TabPane_;

interface TabsContainerProps<Key extends string = string> {
  defaultActiveKey: Key;
  onChange: ({ key }: { key: Key }) => void;
  tabWidth?: React.CSSProperties['width'];
}
const TabsContainer_: React.FC<TabsContainerProps & Omit<React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>, 'onChange'>> = ({
  defaultActiveKey,
  onChange,
  className,
  children,
  tabWidth,
  ...props
}) => {
  const [selectedKey, setSelectedKey] = useState(defaultActiveKey);
  const containerRef = useRef<HTMLDivElement>(null);
  const [indicatorStyle, setIndicatorStyle] = useState<{ left: number; width: number }>({ left: 0, width: 0 });

  const onChangeKey = useCallback(
    ({ key }: { key: string }) => {
      setSelectedKey(key);
      onChange({ key });
    },
    [onChange],
  );

  // A new `defaultActiveKey` selects that tab (render-phase, so the highlight never lags)...
  useOnValueChange(defaultActiveKey, setSelectedKey);

  // ...and notifies the parent. That notification is a genuine external side effect, so it
  // stays in an effect — but it no longer carries a setState with it.
  // (useStableEvent, not React's useEffectEvent: that one goes stale inside memo components — see the hook.)
  const notifyDefaultActiveKey = useStableEvent((key: string) => onChange({ key }));
  useEffect(() => {
    notifyDefaultActiveKey(defaultActiveKey);
  }, [defaultActiveKey, notifyDefaultActiveKey]);

  // Update indicator position by measuring the selected tab's DOM element
  useEffect(() => {
    if (!containerRef.current) return;
    const container = containerRef.current;
    // Find the tab element by data attribute
    const tabEl = container.querySelector(`[data-tab-key="${selectedKey}"]`) as HTMLElement | null;
    if (tabEl) {
      setIndicatorStyle({ left: tabEl.offsetLeft, width: tabEl.offsetWidth });
    }
  }, [selectedKey]);

  return (
    <div ref={containerRef} className={classNames('flex w-full relative h-12 flex-shrink-0', className)} {...props}>
      {React.Children.map(children, (child, index) => {
        if (React.isValidElement<TabPaneProps>(child)) {
          const key = child.key as string;
          const cloneProps: Partial<TabPaneProps> & Record<string, unknown> = {
            key,
            customKey: key,
            'data-tab-key': key,
            onClick: ({ key }: { key: string }) => {
              if (child.props.onClick) child.props.onClick({ key });
              onChangeKey({ key });
            },
            selected: selectedKey ? key === selectedKey : index === 0,
            style: {
              ...child.props.style,
              ...(tabWidth ? { width: tabWidth } : { flex: '1 1 0', minWidth: 0 }),
            },
            className: classNames(key === selectedKey ? 'dark:text-white' : 'text-gray-500', child.props.className),
          };
          return React.cloneElement(child, cloneProps as Partial<TabPaneProps>);
        }
        return null;
      })}
      <div className="absolute bottom-px bg-gray-300 h-px transition-all w-full"></div>
      <div
        className="absolute bottom-0 bg-black dark:bg-white transition-all duration-200"
        style={{
          width: indicatorStyle.width,
          left: indicatorStyle.left,
          height: '3px',
        }}
      ></div>
    </div>
  );
};

export const TabsContainer = memo(TabsContainer_) as typeof TabsContainer_;
